import { useEffect, useState } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { CurrencyInput } from '@components/CurrencyInput';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmLeadService } from '../../kanban-board/services/crm-lead.service';
import { LeadResponse } from '../../kanban-board/services/responses/lead.response';

interface Props {
    lead: LeadResponse;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
}

/// Dashboard soma.
/// Valor estimado e previsão de fechamento: o que o cartão de previsão do Dashboard soma.
export const LeadDealModal = ({ lead, open, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [value, setValue] = useState<number | null>(null);
    const [date, setDate] = useState('');
    const [saving, setSaving] = useState(false);

    // Só ao abrir e ao trocar de lead: recarregar com o modal aberto apagaria o digitado.
    useEffect(() => {
        if (!open) return;

        setValue(lead.value ?? null);
        setDate(lead.expectedCloseDate ?? '');
    }, [open, lead.id]);

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
        '& .MuiFormLabel-root': { color: cc.textSecondary },
    };

    const save = async () => {
        setSaving(true);

        try {
            await new CrmLeadService().updateDeal(lead.id, {
                value,
                expectedCloseDate: date || null,
            });
            showAlert(t('general.atualizacao'), 'success', 2000);
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('telaLead.editarNegocio')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {lead.contactName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 2, pt: 2 }}>
                <CurrencyInput
                    fullWidth
                    value={value}
                    onChange={setValue}
                    label={t('crmKanban.valor')}
                    sx={field}
                />

                <TextField
                    fullWidth
                    type="date"
                    label={t('telaLead.previsaoFechamento')}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    helperText={t('telaLead.previsaoAjuda')}
                    sx={field}
                />
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton variant="gradient" color="success" onClick={save} disabled={saving}>
                    {t('general.salvar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

/// "2026-09-30" vira 30/09/2026 sem Date: new Date() de ISO é meia-noite UTC e volta um dia.
export const formatDateOnly = (value?: string | null) => {
    if (!value) return undefined;

    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};
